import React, { useState, useEffect, useRef } from 'react';
import { useLanguage } from '../context/LanguageContext';
import { useAuth } from '../context/AuthContext';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import axios from 'axios';
import { Send, Bot, User as UserIcon } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function generateUUID() {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
    const r = Math.random() * 16 | 0;
    const v = c === 'x' ? r : (r & 0x3 | 0x8);
    return v.toString(16);
  });
}

const AIMentor = () => {
  const { t, language } = useLanguage();
  const { user, token } = useAuth();
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [sessionId] = useState(() => {
    const stored = localStorage.getItem('ai_session_id');
    if (stored) return stored;
    const newId = generateUUID();
    localStorage.setItem('ai_session_id', newId);
    return newId;
  });
  const messagesEndRef = useRef(null);

  const starterPromptsRo = [
    'Cum fac primii 1000€ în plus pe lună?',
    'Vreau să-mi fac un buget',
    'Ce înseamnă FIRE?'
  ];

  const starterPromptsEn = [
    'Help me calculate my savings rate',
    'How to start a side hustle?',
    'Explain financial independence'
  ];

  const starterPrompts = language === 'ro' ? starterPromptsRo : starterPromptsEn;

  useEffect(() => {
    loadChatHistory();
  }, [sessionId]);

  const loadChatHistory = async () => {
    try {
      const response = await axios.get(`${API}/ai/chat/history/${sessionId}`);
      if (response.data && response.data.length > 0) {
        setMessages(response.data);
      }
    } catch (error) {
      console.error('Failed to load chat history:', error);
    }
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const handleSend = async (messageText = input) => {
    if (!messageText.trim()) return;

    const userMessage = {
      role: 'user',
      content: messageText,
      timestamp: new Date().toISOString()
    };

    setMessages((prev) => [...prev, userMessage]);
    setInput('');
    setLoading(true);

    try {
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const response = await axios.post(
        `${API}/ai/chat`,
        {
          message: messageText,
          session_id: sessionId,
          language
        },
        { headers }
      );

      const aiMessage = {
        role: 'assistant',
        content: response.data.response,
        timestamp: new Date().toISOString()
      };

      setMessages((prev) => [...prev, aiMessage]);
    } catch (error) {
      console.error('Failed to send message:', error);
      toast.error(language === 'ro' ? 'Eroare la trimiterea mesajului' : 'Error sending message');
      const errorMessage = {
        role: 'assistant',
        content: language === 'ro'
          ? 'Scuze, am întâmpinat o eroare. Te rog încearcă din nou.'
          : 'Sorry, I encountered an error. Please try again.',
        timestamp: new Date().toISOString()
      };
      setMessages((prev) => [...prev, errorMessage]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 top-16 bg-gradient-to-br from-blue-50 via-mint-50 to-white flex flex-col overflow-hidden">
      {/* Header - Fixed height */}
      <div className="flex-shrink-0 px-4 py-4 text-center">
        <h1 className="text-xl sm:text-2xl font-bold text-slate-900" data-testid="ai-mentor-title">
          {t('ai.title')}
        </h1>
        <p className="text-sm text-slate-600">{t('ai.subtitle')}</p>
      </div>

      {/* Chat Area - Fills remaining space */}
      <div className="flex-1 flex flex-col min-h-0 mx-4 mb-4 bg-white/90 backdrop-blur-sm rounded-2xl shadow-2xl overflow-hidden">
        {/* Messages - Scrollable */}
        <div className="flex-1 overflow-y-auto p-4 space-y-3" data-testid="chat-messages">
          {messages.length === 0 && (
            <div className="flex h-full items-center justify-center text-center p-4">
              <div>
                <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-gradient-to-br from-blue-500 to-mint-400">
                  <Bot className="h-8 w-8 text-white" />
                </div>
                <h3 className="mb-2 text-lg font-semibold text-slate-900">
                  {language === 'ro' ? 'Salut! Sunt AI Money Mentor.' : 'Hi! I\'m your AI Money Mentor.'}
                </h3>
                <p className="text-sm text-slate-600">
                  {language === 'ro'
                    ? 'Întreabă-mă orice despre bani!'
                    : 'Ask me anything about money!'}
                </p>
              </div>
            </div>
          )}

          {messages.map((msg, idx) => (
            <div
              key={idx}
              className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
              data-testid={`message-${msg.role}`}
            >
              <div
                className={`flex max-w-[85%] items-start gap-2 ${
                  msg.role === 'user' ? 'flex-row-reverse' : ''
                }`}
              >
                <div
                  className={`flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-full ${
                    msg.role === 'user'
                      ? 'bg-gradient-to-br from-blue-600 to-blue-700'
                      : 'bg-gradient-to-br from-mint-400 to-mint-500'
                  }`}
                >
                  {msg.role === 'user' ? (
                    <UserIcon className="h-4 w-4 text-white" />
                  ) : (
                    <Bot className="h-4 w-4 text-white" />
                  )}
                </div>
                <div
                  className={`rounded-2xl px-3 py-2 shadow-sm ${
                    msg.role === 'user'
                      ? 'bg-gradient-to-br from-blue-600 to-blue-700 text-white'
                      : 'bg-slate-100 text-slate-900'
                  }`}
                >
                  <p className="whitespace-pre-wrap text-sm">{msg.content}</p>
                </div>
              </div>
            </div>
          ))}

          {loading && (
            <div className="flex justify-start">
              <div className="flex items-start gap-2">
                <div className="flex h-7 w-7 items-center justify-center rounded-full bg-gradient-to-br from-mint-400 to-mint-500">
                  <Bot className="h-4 w-4 text-white" />
                </div>
                <div className="rounded-2xl bg-slate-100 px-4 py-3">
                  <div className="flex space-x-1">
                    <div className="h-2 w-2 animate-bounce rounded-full bg-blue-500"></div>
                    <div className="h-2 w-2 animate-bounce rounded-full bg-blue-500" style={{ animationDelay: '0.2s' }}></div>
                    <div className="h-2 w-2 animate-bounce rounded-full bg-mint-500" style={{ animationDelay: '0.4s' }}></div>
                  </div>
                </div>
              </div>
            </div>
          )}
          <div ref={messagesEndRef} />
        </div>

        {/* Starter Prompts - Only when no messages */}
        {messages.length === 0 && (
          <div className="flex-shrink-0 px-4 pb-2">
            <p className="text-xs text-center text-slate-500 mb-2">{t('ai.starterPrompts')}</p>
            <div className="flex flex-wrap justify-center gap-2">
              {starterPrompts.map((prompt, idx) => (
                <Badge
                  key={idx}
                  className="cursor-pointer bg-slate-100 border border-slate-200 px-3 py-1.5 text-xs text-slate-700 hover:bg-blue-600 hover:text-white hover:border-blue-600 transition-all"
                  onClick={() => handleSend(prompt)}
                  data-testid={`starter-prompt-${idx}`}
                >
                  {prompt}
                </Badge>
              ))}
            </div>
          </div>
        )}

        {/* Input - Fixed at bottom */}
        <div className="flex-shrink-0 border-t border-slate-200 bg-white p-3">
          <form
            onSubmit={(e) => {
              e.preventDefault();
              handleSend();
            }}
            className="flex gap-2"
          >
            <Input
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder={t('ai.placeholder')}
              disabled={loading}
              data-testid="chat-input"
              className="flex-1 bg-slate-50 border-slate-200 text-sm"
            />
            <Button
              type="submit"
              disabled={loading || !input.trim()}
              className="bg-blue-600 hover:bg-blue-700 text-white px-4"
              data-testid="send-button"
            >
              <Send className="h-4 w-4" />
            </Button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default AIMentor;
